import { useMemo } from 'react';
import type { MessageClass } from '@cotera/sdk/core';
import { getMessageFooters } from './registry';
import type { MessageFooterHandler } from './types';
import type { HijackerQueueState } from '../input-hijackers';
import {
  useInScopeModel,
  useWatchableValue,
  Watchable,
} from '../../actions/framework';
import { CocoChatModel } from '@cotera/client/app/v1/chat/models/coco-chat.model';
import { ChatStatusFrameOutput } from '@cotera/api';

/**
 * Hook to find the active message footer based on the current messages.
 * Evaluates all registered footers and returns the highest priority one
 * where shouldShow returns true.
 *
 * If multiple footers have the same priority and showLatestOnly is true,
 * only the most recently registered one is returned.
 *
 * @param messages - Current conversation messages
 * @returns The active footer handler, or null if none should be shown
 */
export function useHijackerPlaceHolder(queueState: HijackerQueueState): {
  id: string;
  content: string;
} | null {
  const activeInputHijacker = queueState.activeItem;
  if (activeInputHijacker !== null) {
    return {
      id: activeInputHijacker.id,
      content:
        activeInputHijacker.inputPlaceholder ?? 'Or do something else...',
    };
  }

  return null;
}

const DEFAULT_MESSAGE_WATCHABLE = Watchable.fromValue<MessageClass[]>([]);
const DEFAULT_STACK_FRAMES_WATCHABLE = Watchable.fromValue<
  ChatStatusFrameOutput[]
>([]);

/**
 * Hook to get all active footer items (for cases where you want to show multiple).
 * Returns active footers respecting showLatestOnly for each priority level.
 */
export function useMessageFooterItems(
  conversationId: string
): Array<{ id: string; content: React.ReactNode }> {
  const conversation = useInScopeModel(CocoChatModel, conversationId)!;
  const messages = useWatchableValue(
    conversation?.messagesWatchable ?? DEFAULT_MESSAGE_WATCHABLE
  );
  const stackFrames = useWatchableValue(
    conversation?.stackFramesWatchable ?? DEFAULT_STACK_FRAMES_WATCHABLE
  );

  return useMemo(() => {
    const footers = getMessageFooters();
    const activeFooters: MessageFooterHandler[] = [];

    for (const footer of footers) {
      if (footer.shouldShow(messages, stackFrames)) {
        activeFooters.push(footer);
      }
    }

    if (activeFooters.length === 0) {
      return [];
    }

    // Sort by priority (highest first)
    activeFooters.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));

    // Handle showLatestOnly for each priority level
    const seenPriorities = new Set<number>();
    const result: Array<{ id: string; content: React.ReactNode }> = [];

    for (const footer of activeFooters) {
      const priority = footer.priority ?? 0;
      if (footer.showLatestOnly && seenPriorities.has(priority)) {
        continue;
      }
      seenPriorities.add(priority);
      result.push({
        id: footer.id,
        content: footer.render({ messages }),
      });
    }

    return result;
  }, [messages, stackFrames]);
}
